import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import AsistenteService from '../services/AsistenteService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import UserSessionMenu from './common/UserSessionMenu';
import ColegioService from '../services/ColegioService';
import materiaService from '../services/MateriaService';
import { hasOpenAIChatsEnabled, isFeatureEnabled, normalizeRoleName } from '../utils/adminMenu';
import './NewAssistant.css';
import ProfessorMenu from './common/menus/ProfessorMenu';

const isProfessorUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return roleName === 'profesor' || roleName.includes('profesor') || roleName.includes('profe');
};

const VALIDATION_ERROR_MESSAGE = 'No pudimos procesar los archivos. Verifica que todos sean PDF válidos y que no superen los límites permitidos.';
const PROMPT_FEATURES = [
  'Usa exclusivamente los PDFs adjuntos (RAG obligatorio).',
  'Ejecuta file_search antes de responder consultas académicas.',
  'Si no hay evidencia en los PDFs, responde con el mensaje de falta de información.',
  'Respuestas académicas en HTML con citas de PDF y número de página.',
  'Si el usuario pide audio, la locución debe leer solo el contenido visible y no las etiquetas HTML.',
  'Puede responder saludos o uso del asistente sin citar PDFs.',
  'Imágenes o esquemas solo si existen en PDFs o son representaciones fieles con aviso.',
  'Puede armar planes de estudio y actuar como tutor orientado a ayudar al alumno.'
];

const getFriendlyApiErrorMessage = (error, fallback) => {
  if (!error || !error.message) {
    return fallback;
  }
  const normalized = error.message.toLowerCase();
  if (normalized.includes('validation')) {
    return VALIDATION_ERROR_MESSAGE;
  }
  return error.message;
};

const getMateriaOptionLabel = (materia) => {
  const nombre = materia?.nombre || materia?.Nombre || 'Materia';
  const area = materia?.nombreArea || materia?.NombreArea || '';
  const valorAnio = materia?.valorAnio ?? materia?.ValorAnio ?? null;
  const division = String(materia?.division || materia?.Division || '').trim();
  const scope = valorAnio ? `${valorAnio}°${division ? ` ${division}` : ''}` : division;
  const parts = [area, nombre, scope].filter(Boolean);
  return parts.join(' · ');
};

const NewAssistant = () => {
  const { user, logout, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isProfesor = isProfessorUser(user);
  const isAdmin = normalizeRoleName(user).includes('admin');
  const hasModuloAlumnos = useMemo(() => {
    const flags = ColegioService.getFeatureFlags();
    return isFeatureEnabled(flags, 'ModuloAlumnos');
  }, []);
  const hasAgentesClasicos = useMemo(() => {
    const flags = ColegioService.getFeatureFlags();
    return isFeatureEnabled(flags, 'PermiteAgentesClasicosGPT');
  }, []);
  const hasOpenAIChats = useMemo(() => {
    const flags = ColegioService.getFeatureFlags();
    return hasOpenAIChatsEnabled(flags);
  }, []);
  
  // Estados del componente
  const [isLoading, setIsLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [formData, setFormData] = useState({
    nombre: '',
    nicknameUrl: '',
    descripcion: '',
    promptExtra: '',
    idColegio: '',
    idArea: '',
    idMateria: '',
    idUnidad: '',
    archivosPdf: [],
    publico: true,
    passwordAsistente: '',
    esPropio: false
  });
  const [materias, setMaterias] = useState([]);
  const [materiasLoading, setMateriasLoading] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [unidades, setUnidades] = useState([]);
  const [unidadesLoading, setUnidadesLoading] = useState(false);
  const [unidadesError, setUnidadesError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Validar permisos al montar el componente
  useEffect(() => {
    if (authLoading || isAdmin) return;
    validateUserPermissions();
  }, [user, isProfesor, authLoading, isAdmin]);

  useEffect(() => {
    if (authLoading || !isAdmin) return;
    navigate('/admin/asistentes/nuevo', { replace: true });
  }, [authLoading, isAdmin, navigate]);

  const validateUserPermissions = () => {
    try {
      if (!isProfesor) {
        setHasPermission(false);
        setAlert({
          show: true,
          title: 'Acceso restringido',
          message: 'Esta sección está disponible únicamente para usuarios con rol Profesor.',
          type: 'warning'
        });
        setIsLoading(false);
        return;
      }

      if (!hasOpenAIChats) {
        setHasPermission(false);
        setAlert({
          show: true,
          title: 'Chats OpenAI deshabilitados',
          message: 'Este colegio no tiene habilitada la feature flag PermiteChatsOpenAI.',
          type: 'warning'
        });
        setIsLoading(false);
        return;
      }

      // Verificar si hay usuario autenticado
      if (!user) {
        setAlert({
          show: true,
          title: 'Sesión requerida',
          message: 'Debes iniciar sesión para acceder a esta funcionalidad',
          type: 'error'
        });
        setHasPermission(false);
        setIsLoading(false);
        
        // Redirigir al login después de un breve retraso
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      setHasPermission(true);
      
    } catch (error) {
      console.error('Error al validar permisos:', error);
      setHasPermission(false);
      setAlert({
        show: true,
        title: 'Error de permisos',
        message: 'Error al validar permisos. Inténtalo nuevamente.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isProfesor || isAdmin || !hasOpenAIChats) return;
    const activeColegioId = ColegioService.getActiveColegioId();
    setFormData((prev) => ({ ...prev, idColegio: activeColegioId || prev.idColegio }));
    if (!activeColegioId) return;

    const fetchMaterias = async () => {
      setMateriasLoading(true);
      setMateriasError('');
      try {
        const list = await materiaService.getMisMaterias({ idColegio: activeColegioId });
        setMaterias(Array.isArray(list) ? list : []);
      } catch (error) {
        setMateriasError(error.message || 'No pudimos cargar tus materias.');
      } finally {
        setMateriasLoading(false);
      }
    };

    fetchMaterias();
  }, [isProfesor, isAdmin, hasOpenAIChats]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMateriaChange = (value) => {
    const selected = materias.find((m) => (m.id || m.Id) === value);
    const idArea = selected?.idArea || selected?.IdArea || '';
    setFormData((prev) => ({
      ...prev,
      idMateria: value,
      idArea,
      idUnidad: ''
    }));
  };

  const handleNicknameChange = (value) => {
    const normalized = value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-');
    handleInputChange('nicknameUrl', normalized);
  };

  // Manejar drag and drop de archivos
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  const addFiles = (newFiles) => {
    // Filtrar solo archivos PDF
    const pdfFiles = newFiles.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== newFiles.length) {
      setAlert({
        show: true,
        title: 'Archivos inválidos',
        message: 'Solo se permiten archivos PDF',
        type: 'warning'
      });
    }

    setFormData(prev => {
      const currentFiles = prev.archivosPdf;
      const totalFiles = [...currentFiles, ...pdfFiles];
      
      // Validar máximo 20 archivos
      if (totalFiles.length > 20) {
        setAlert({
          show: true,
          title: 'Límite de archivos',
          message: 'No se pueden subir más de 20 archivos PDF',
          type: 'error'
        });
        return prev; // No actualizar si excedel límite
      }
      
      return {
        ...prev,
        archivosPdf: totalFiles
      };
    });
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      archivosPdf: prev.archivosPdf.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.nombre.trim()) {
      setAlert({
        show: true,
        title: 'Nombre requerido',
        message: 'El nombre del asistente es obligatorio',
        type: 'error'
      });
      return;
    }

    if (materiasLoading) {
      setAlert({
        show: true,
        title: 'Materias cargando',
        message: 'Esperá a que carguen tus materias para continuar.',
        type: 'warning'
      });
      return;
    }

    if (formData.archivosPdf.length === 0) {
      setAlert({
        show: true,
        title: 'Archivos requeridos',
        message: 'Debes subir al menos un archivo PDF',
        type: 'error'
      });
      return;
    }

    if (formData.archivosPdf.length > 20) {
      setAlert({
        show: true,
        title: 'Límite de archivos',
        message: 'No puedes subir más de 20 archivos PDF',
        type: 'error'
      });
      return;
    }
    if (!formData.publico && !formData.passwordAsistente.trim()) {
      setAlert({
        show: true,
        title: 'Password requerido',
        message: 'Debes ingresar un password cuando el asistente es privado.',
        type: 'error'
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Mostrar mensaje inicial
      setAlert({
        show: true,
        title: 'Creando asistente',
        message: 'Creando asistente... Este proceso puede demorar unos minutos debido a los archivos que se deben procesar.',
        type: 'info'
      });
      
      // Crear FormData para envío multipart
      const formDataToSend = new FormData();
      formDataToSend.append('nombre', formData.nombre);
      if (formData.nicknameUrl.trim()) {
        formDataToSend.append('nicknameUrl', formData.nicknameUrl.trim());
      }
      if (formData.idColegio) {
        formDataToSend.append('idColegio', formData.idColegio);
      }
      if (formData.idArea) {
        formDataToSend.append('idArea', formData.idArea);
      }
      if (formData.idMateria) {
        formDataToSend.append('idMateria', formData.idMateria);
      }
      if (formData.idMateria) {
        formDataToSend.append('idUnidad', formData.idUnidad || '');
      }
      
      // Agregar todos los archivos PDF
      formData.archivosPdf.forEach(file => {
        formDataToSend.append('archivosPdf', file);
      });
      if (formData.descripcion) {
        formDataToSend.append('descripcion', formData.descripcion);
      }
      if (formData.promptExtra.trim()) {
        formDataToSend.append('promptExtra', formData.promptExtra.trim());
      }
      formDataToSend.append('publico', String(formData.publico));
      formDataToSend.append('conPassword', String(!formData.publico));
      formDataToSend.append('esPropio', String(formData.esPropio));
      if (!formData.publico && formData.passwordAsistente.trim()) {
        formDataToSend.append('passwordAsistente', formData.passwordAsistente.trim());
      }

      // Hacer la petición POST
      await AsistenteService.crearAsistente(formDataToSend);
      
      setAlert({
        show: true,
        title: 'Asistente creado',
        message: 'Asistente creado exitosamente',
        type: 'success'
      });
      
      // Limpiar formulario después del éxito
      setTimeout(() => {
        setFormData({
          nombre: '',
          nicknameUrl: '',
          descripcion: '',
          promptExtra: '',
          idColegio: ColegioService.getActiveColegioId() || '',
          idArea: '',
          idMateria: '',
          idUnidad: '',
          archivosPdf: [],
          publico: true,
          passwordAsistente: '',
          esPropio: false
        });
        
        // Opcional: redirigir al dashboard
        // navigate('/dashboard');
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error al crear asistente:', error);
      setAlert({
        show: true,
        title: 'Error al crear asistente',
        message: getFriendlyApiErrorMessage(error, 'No pudimos crear el asistente. Inténtalo nuevamente.'),
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!formData.idMateria) {
      setUnidades([]);
      setUnidadesError('');
      setUnidadesLoading(false);
      return;
    }

    let isMounted = true;
    setUnidadesLoading(true);
    setUnidadesError('');

    materiaService.getUnidadesActivas(formData.idMateria)
      .then((list) => {
        if (!isMounted) return;
        setUnidades(Array.isArray(list) ? list : []);
      })
      .catch((error) => {
        if (!isMounted) return;
        setUnidadesError(error.message || 'No pudimos cargar las unidades.');
        setUnidades([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setUnidadesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [formData.idMateria]);

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const handleGoBack = () => {
    navigate('/dashboard');
  };

  const handleLogout = () => {
    logout();
  };

  // Mostrar loading mientras se validan permisos
  if (authLoading || isLoading) {
    return (
      <div className="new-assistant-container">
        <div className="loading-container">
          <LoadingSpinner message="Validando permisos..." />
        </div>
      </div>
    );
  }

  if (isAdmin) {
    return (
      <div className="new-assistant-container">
        <div className="loading-container">
          <LoadingSpinner message="Redirigiendo a Asistentes OpenAI..." />
        </div>
      </div>
    );
  }

  // Redirigir al dashboard si no tiene permisos (mostrar alerta allí)
  if (!hasPermission && hasOpenAIChats) {
    navigate('/dashboard', { replace: true, state: { deniedCreateAssistant: true } });
    return null;
  }

  if (!hasOpenAIChats) {
    return (
      <div className="new-assistant-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '2rem auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Chats OpenAI deshabilitados</h2>
          <p>Este colegio no tiene habilitada la feature flag `PermiteChatsOpenAI`.</p>
        </div>
      </div>
    );
  }

  // Mostrar formulario de creación si tiene permisos
  return (
    <div className="new-dashboard">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>
        
        <ProfessorMenu
          activeId="mis-asistentes-openai"
          hasOpenAIChats={hasOpenAIChats}
          hasAgentesClasicos={hasAgentesClasicos}
          hasModuloAlumnos={hasModuloAlumnos}
          onNavigate={(item) => item?.path && navigate(item.path)}
        />
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.rol || user?.role || 'Profesor'}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">
              <i className="fas fa-robot"></i>
              Crear Nuevo Asistente OpenAI
            </h1>
            <p className="page-subtitle">Sube archivos PDF para crear un asistente personalizado</p>
          </div>
          
          <div className="header-right">
            <SolicitudesDropdown />
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={handleLogout} />
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          <div className="new-assistant-content">
            <form onSubmit={handleSubmit} className="assistant-form">
              <div className="assistant-form-grid">
                <div className="assistant-form-panel">
              {/* Información básica */}
              <div className="form-section basic-info-section">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-robot"></i>
                  </div>
                  <div className="section-text">
                    <h3 className="section-title">Información del Asistente</h3>
                    <p className="section-description">Define el nombre y características básicas de tu asistente IA</p>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="nombre" className="form-label">
                    <i className="fas fa-tag"></i>
                    Nombre del Asistente *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    className="form-input enhanced"
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    placeholder="Ej: Asistente de Lógica y Estructuras Discretas"
                    required
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-lightbulb"></i>
                    Escoge un nombre descriptivo que refleje el área de conocimiento
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="idMateria" className="form-label">
                    <i className="fas fa-book"></i>
                    Materia (opcional)
                  </label>
                  <select
                    id="idMateria"
                    className="form-select enhanced"
                    value={formData.idMateria}
                    onChange={(e) => handleMateriaChange(e.target.value)}
                    disabled={isSubmitting || materiasLoading}
                  >
                    <option value="">Sin materia (asistente institucional)</option>
                    {materias.map((materia) => {
                      const id = materia.id || materia.Id;
                      return (
                        <option key={id} value={id}>
                          {getMateriaOptionLabel(materia)}
                        </option>
                      );
                    })}
                  </select>
                  {materiasLoading && (
                    <div className="input-hint">
                      <i className="fas fa-spinner fa-spin"></i>
                      Cargando materias asignadas...
                    </div>
                  )}
                  {materiasError && (
                    <div className="input-hint" style={{ color: '#dc2626' }}>
                      <i className="fas fa-triangle-exclamation"></i>
                      {materiasError}
                    </div>
                  )}
                  {!materiasLoading && !materiasError && materias.length === 0 && (
                    <div className="input-hint" style={{ color: '#b45309' }}>
                      <i className="fas fa-circle-info"></i>
                      No tienes materias asignadas. Puedes crear un asistente institucional sin materia.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="idUnidad" className="form-label">
                    <i className="fas fa-layer-group"></i>
                    Unidad (opcional)
                  </label>
                  <select
                    id="idUnidad"
                    className="form-select enhanced"
                    value={formData.idUnidad}
                    onChange={(e) => handleInputChange('idUnidad', e.target.value)}
                    disabled={isSubmitting || unidadesLoading || !formData.idMateria}
                  >
                    <option value="">Sin unidad</option>
                    {unidades.map((unidad) => {
                      const id = unidad.id || unidad.Id;
                      const nombre = unidad.nombre || unidad.Nombre || 'Unidad';
                      return (
                        <option key={id} value={id}>
                          {nombre}
                        </option>
                      );
                    })}
                  </select>
                  {!formData.idMateria && (
                    <div className="input-hint">
                      <i className="fas fa-circle-info"></i>
                      Si dejas la materia vacía, el asistente se crea como institucional.
                    </div>
                  )}
                  {unidadesLoading && (
                    <div className="input-hint">
                      <i className="fas fa-spinner fa-spin"></i>
                      Cargando unidades...
                    </div>
                  )}
                  {unidadesError && (
                    <div className="input-hint" style={{ color: '#dc2626' }}>
                      <i className="fas fa-triangle-exclamation"></i>
                      {unidadesError}
                    </div>
                  )}
                  {!unidadesLoading && !unidadesError && formData.idMateria && unidades.length === 0 && (
                    <div className="input-hint" style={{ color: '#b45309' }}>
                      <i className="fas fa-circle-info"></i>
                      No hay unidades activas para esta materia.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="nicknameUrl" className="form-label">
                    <i className="fas fa-link"></i>
                    Nickname para URL
                  </label>
                  <input
                    type="text"
                    id="nicknameUrl"
                    className="form-input enhanced"
                    value={formData.nicknameUrl}
                    onChange={(e) => handleNicknameChange(e.target.value)}
                    placeholder="ej: algebra-lineal-1"
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-info-circle"></i>
                    Solo letras, números y guiones. Se usará para compartir el acceso.
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="descripcion" className="form-label">
                    <i className="fas fa-align-left"></i>
                    Descripción del Asistente
                  </label>
                  <textarea
                    id="descripcion"
                    className="form-input enhanced"
                    rows="4"
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    placeholder="Describe brevemente el propósito o alcance del asistente"
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-info-circle"></i>
                    Esto ayuda a identificar rápidamente el objetivo del asistente
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-eye"></i>
                    Visibilidad y acceso
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.publico}
                        onChange={(e) => {
                          const nextValue = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            publico: nextValue,
                            passwordAsistente: nextValue ? '' : prev.passwordAsistente
                          }));
                        }}
                        disabled={isSubmitting}
                      />
                      <span>Asistente público</span>
                    </label>
                  </div>
                </div>

                {!formData.publico && (
                  <div className="form-group">
                    <label htmlFor="passwordAsistente" className="form-label">
                      <i className="fas fa-key"></i>
                      Password del Asistente
                    </label>
                    <input
                      id="passwordAsistente"
                      type="text"
                      className="form-input enhanced"
                      value={formData.passwordAsistente}
                      onChange={(e) => handleInputChange('passwordAsistente', e.target.value)}
                      placeholder="Ingresa el password (obligatorio)"
                      disabled={isSubmitting}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-user-lock"></i>
                    Uso del asistente
                  </label>
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.9rem',
                      alignItems: 'flex-start',
                      padding: '1rem 1.1rem',
                      borderRadius: '14px',
                      border: `1px solid ${formData.esPropio ? '#fdba74' : '#bfdbfe'}`,
                      background: formData.esPropio ? '#fff7ed' : '#eff6ff'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.esPropio}
                      onChange={(e) => handleInputChange('esPropio', e.target.checked)}
                      disabled={isSubmitting}
                      style={{ marginTop: '0.15rem' }}
                    />
                    <div style={{ display: 'grid', gap: '0.3rem' }}>
                      <strong style={{ color: '#0f172a' }}>Asistente propio</strong>
                      <span style={{ color: '#475569', lineHeight: 1.5 }}>
                        {formData.esPropio
                          ? 'Este asistente será solo para uso personal del docente. No podrá compartirse con alumnos por medio del aula.'
                          : 'Este asistente podrá ser visible para los estudiantes por medio del aula, según la configuración restante del asistente.'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Archivos PDF */}
              <div className="form-section files-section">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-file-pdf"></i>
                  </div>
                  <div className="section-text">
                    <h3 className="section-title">Documentos de Conocimiento</h3>
                    <p className="section-description">Sube los archivos PDF que contengan la información que tu asistente debe conocer</p>
                  </div>
                </div>
                
                <div className="files-upload-container">
                  <div 
                    className={`file-dropzone ${isDragOver ? 'dragover' : ''} ${formData.archivosPdf.length > 0 ? 'has-files' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="dropzone-border">
                      <div className="dropzone-content">
                        <div className="dropzone-icon">
                          <i className="fas fa-cloud-upload-alt"></i>
                        </div>
                        <div className="dropzone-text">
                          <h4>Arrastra y suelta tus archivos PDF aquí</h4>
                          <p>Los archivos se procesarán automáticamente para entrenar a tu asistente</p>
                        </div>
                        <div className="dropzone-divider">
                          <span>o</span>
                        </div>
                        <label className="file-input-label">
                          <input
                            type="file"
                            multiple
                            accept=".pdf"
                            onChange={handleFileInput}
                            disabled={isSubmitting}
                            style={{ display: 'none' }}
                          />
                          <span className="btn btn-upload">
                            <i className="fas fa-folder-open"></i>
                            Seleccionar Archivos PDF
                          </span>
                        </label>
                        <div className="upload-limits">
                          <div className="limit-item">
                            <i className="fas fa-file-alt"></i>
                            <span>Máximo 20 archivos</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-file-pdf"></i>
                            <span>Solo formato PDF</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-weight-hanging"></i>
                            <span>Hasta 10MB por archivo</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lista de archivos seleccionados */}
                {formData.archivosPdf.length > 0 && (
                  <div className="selected-files">
                    <h4>Archivos seleccionados ({formData.archivosPdf.length}/20):</h4>
                    <div className="files-list">
                      {formData.archivosPdf.map((file, index) => (
                        <div key={index} className="file-item">
                          <div className="file-info">
                            <i className="fas fa-file-pdf"></i>
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                          <button
                            type="button"
                            className="remove-file-btn"
                            onClick={() => removeFile(index)}
                            disabled={isSubmitting}
                            title="Eliminar archivo"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

                </div>

                <aside className="assistant-prompt-panel">
                  <div className="prompt-panel-header">
                    <i className="fas fa-clipboard-list"></i>
                    <div>
                      <h3>Comportamiento del asistente</h3>
                      <p>Resumen del prompt base configurado en el backend.</p>
                    </div>
                  </div>
                  <ul className="prompt-features-list">
                    {PROMPT_FEATURES.map((item) => (
                      <li key={item}>
                        <i className="fas fa-check-circle"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="prompt-extra-field">
                    <label htmlFor="promptExtra" className="form-label">
                      <i className="fas fa-pen"></i>
                      Aclaraciones extra (opcional)
                    </label>
                    <textarea
                      id="promptExtra"
                      className="form-input enhanced"
                      rows="6"
                      value={formData.promptExtra}
                      onChange={(e) => handleInputChange('promptExtra', e.target.value)}
                      placeholder="Ej: prioriza ejemplos resueltos, responde en español neutro..."
                      disabled={isSubmitting}
                    />
                    <div className="input-hint">
                      <i className="fas fa-info-circle"></i>
                      Estas reglas se agregarán al prompt base del asistente.
                    </div>
                  </div>
                </aside>
              </div>

              {/* Botones de Acción */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleGoBack}
                  disabled={isSubmitting}
                >
                  <i className="fas fa-arrow-left"></i>
                  Volver al Dashboard
                </button>
                
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting || formData.archivosPdf.length === 0}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Creando Asistente...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-robot"></i>
                      Crear Asistente
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewAssistant;
